import { Toast } from 'bootstrap';

export interface AppStudioToastOptions {
    title?: string;
    variant?: string;
    toaster?: string;
    autoHideDelay?: number;
    solid?: boolean;
    href?: string;
}

function resolveToaster(name?: string): Element | null {
    if (typeof document === 'undefined') return null;
    return name
        ? document.querySelector(`[data-toaster="${name}"], [name="${name}"], #${name}`)
        : document.getElementById('toast-container');
}

function safeNotificationHref(value?: string): string | undefined {
    if (!value) return undefined;
    try {
        const url = new URL(value, window.location.href);
        return url.protocol === 'http:' || url.protocol === 'https:' ? value : undefined;
    } catch {
        return undefined;
    }
}

function createToastElement(message: string, options: AppStudioToastOptions): HTMLElement {
    const element = document.createElement('div');
    element.className = `toast${options.variant ? ` border-${options.variant}` : ''}`;
    element.setAttribute('role', 'alert');
    element.setAttribute('aria-live', 'assertive');
    element.setAttribute('aria-atomic', 'true');

    if (options.title) {
        const header = document.createElement('div');
        header.className = `toast-header${options.solid && options.variant ? ` text-bg-${options.variant}` : ''}`;
        const title = document.createElement('strong');
        title.className = 'appstudio-toast-title';
        title.textContent = options.title;
        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'btn-close';
        close.setAttribute('data-bs-dismiss', 'toast');
        close.setAttribute('aria-label', 'Close');
        header.append(title, close);
        element.append(header);
    }

    const body = document.createElement('div');
    body.className = 'toast-body';
    const href = safeNotificationHref(options.href);
    if (href) {
        const link = document.createElement('a');
        link.href = href;
        link.textContent = message;
        body.append(link);
    } else {
        body.textContent = message;
    }
    element.append(body);
    return element;
}

export const appStudioToastService = {
    show(message: string, options: AppStudioToastOptions = {}): Toast | undefined {
        const toaster = resolveToaster(options.toaster) ?? resolveToaster();
        if (!toaster) return undefined;
        const element = createToastElement(message, options);
        toaster.append(element);
        const toast = Toast.getOrCreateInstance(element, {
            autohide: options.autoHideDelay !== 0,
            delay: options.autoHideDelay ?? 5000
        });
        element.addEventListener('hidden.bs.toast', () => element.remove());
        toast.show();
        return toast;
    }
};
